import { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import {
    SmartlyActionItems,
    layoutActions,
    ModalsType,
    canRelease,
    Analytics,
    User,
    GetMessageParams,
} from '@Techedge/digitalportal-shared'
import Sidebar from '../components/Layout/Sidebar/SideBar'
import MainContent from '../components/Layout/MainContent/MainContent'
import { LuxBadgeLabel, useWindowResize, LuxCustomBreadcrumb } from '@lux/wardrobe-lib'
import Accordion from '../components/Layout/Accordion/Accordion'
import {
    ExecutionMessage,
    NEW_ACTIVITY_EXECUTION_NAMESPACE,
} from '../redux/reducers/activity-execution'
import MainInformation from '../components/ActivityExecution/MainInformation/MainInformation'
import Messaging from '../components/ActivityExecution/Messaging/Messaging'
import {
    getMessage,
    getVisitProjectDetails,
    setIsFullScreen,
    setMessageList,
} from '../redux/actions/activity-execution'
import { ProjectApiCreateActivityBody } from '../models/projects-service'
import { setSelectedStores } from '../redux/actions/activity-approval'
import { rejectProject, resetAllForm } from '../redux/actions/activity-creation'
import DialogDuplicate from '../components/UI/Dialogs/DialogDuplicate/DialogDuplicate'
import DialogReject from '../components/UI/Dialogs/DialogReject/DialogReject'
import DialogEditConfirm from '../components/UI/Dialogs/DialogEditConfirm/DialogEditConfirm'
import { statusColors } from '../utils/statusColorMap'
import { executionAnalyticsPageTypeMap } from '../models/analyticsPageTypeMap'
import EditingReason from '../components/ActivityCreation/EditingReason/EditingReason'
import ReassignDialog from '../components/ActivityExecution/ReassignDialog/ReassignDialog'
import { DelegateType } from '../models/assignee'
import VisitProjectSpecs from '../components/ActivityExecution/Visit/VisitProjectSpecs/VisitProjectSpecs'
import VisitExecutionAction from '../components/ActivityExecution/Visit/VisitExecutionAction/VisitExecutionAction'
import VisitFooter from '../components/ActivityExecution/Visit/VisitFooter/VisitFooter'
import CancelledReasons from '../components/ActivityExecution/CancelledReasons/CancelledReasons'
import './TaskCommunicationExecution.scss'
import { getBreadcrumbs } from '@utils/commonFunctions'
export interface VisitExecutionProps {
    messageList: ExecutionMessage[]
    projectId: string
    storeIdList: string[]
    isFullScreen: boolean
    user: User
    projectDetails: ProjectApiCreateActivityBody
    surveyId: string
    setIsFullScreen: (value: boolean) => void
    onClose: () => void
    goToEdit: (projectId: string, storeId: string, isClone: boolean, isEdit?: boolean) => void
    rejectProject: (projectId: string, message: string, successMessage: string) => void
    getProjectDetails: (projectId: string, stores?: string[]) => void
    getMessage: (params: any) => void
}

const VisitExecution = ({
    messageList,
    projectId,
    storeIdList,
    user,
    projectDetails,
    isFullScreen,
    surveyId,
    goToEdit,
    onClose,
    rejectProject,
    getProjectDetails,
    getMessage
}: VisitExecutionProps) => {
    const intl = useIntl()
    const componentClass = 'smart-modals-activity-execution'

    const [activeIndexAction, setActiveIndexAction] = useState<0 | null>(surveyId ? 0 : null)
    const [activeIndexMessaging, setActiveIndexMessaging] = useState<0 | null>(null)
    const [activeIndexProjectSpecs, setActiveIndexProjectSpecs] = useState<0 | null>(0)
    const [activeIndexRedoReasons, setActiveIndexRedoReasons] = useState<0 | null>(null)
    const [localProjectDetails, setLocalProjectDetails] =
        useState<ProjectApiCreateActivityBody>(null)

    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)

    const [showRejectDialog, setShowRejectDialog] = useState(false)
    const [rejectReasons, setRejectReasons] = useState('')

    const [showEditDialogConfirm, setShowEditDialogConfirm] = useState(false)
    const [showReassignDialog, setShowReassignDialog] = useState(false)
    const [projectType, setProjectType] = useState<SmartlyActionItems>()
    const [headerHeight] = useState()
    const [pathArray, setPathArray] = useState<any[]>([])
    const isMobile = useWindowResize()

    const [selectedStoreToExecute, setSelectedStoreToExecute] = useState(null)

    const onDialogConfirm = (confirm) => {
        setShowEditDialogConfirm(false)
        if (confirm) {
            goToEdit(localProjectDetails._id, localProjectDetails.storesIdList[0], false, true)
        }
    }

    const [breadcrumbs, setBreadcrumbs] = useState([])

    const onRejectConfirm = () => {
        setShowRejectDialog(false)
        rejectProject(
            localProjectDetails._id,
            rejectReasons,
            intl.formatMessage({
                id: 'modals.activity.create.reject.success.message',
                defaultMessage: 'Project rejected successfully!',
            })
        )
    }

    // FUNCTIONS

    const onSaveDialogDuplicate = (confirm: boolean) => {
        if (confirm) {
            goToEdit(localProjectDetails._id, localProjectDetails.storesIdList[0], true)
            return
        }
        setShowDuplicateDialog(false)
    }

    const onClickBreadcrumb = (breadcrumb: any) => {
        onClose()
        setBreadcrumbs(breadcrumb)
    }

    // USE EFFECTS

    useEffect(() => {
        if (!!projectType) {
            if (
                canRelease(localProjectDetails.projectType, localProjectDetails) &&
                localProjectDetails?.status === 'toBeReleased'
            ) {
                // release
                Analytics._sendAnalyticsLinkEvent(
                    'Activities',
                    executionAnalyticsPageTypeMap[projectDetails.projectType][projectDetails.module]
                        ?.release,
                    'Static'
                )
                return
            }
            if (
                localProjectDetails?.status !== 'draft' &&
                localProjectDetails?.status !== 'toBeReleased'
            ) {
                // execution
                Analytics._sendAnalyticsLinkEvent(
                    'Activities',
                    executionAnalyticsPageTypeMap[localProjectDetails.projectType][
                        localProjectDetails.module
                    ].execution,
                    'Static'
                )
            }
        }
    }, [projectType])

    useEffect(() => {
        const breadcrumbClick = () => {
            onClose()
        }
        let breadcrumbSelector = document.querySelector(`.${componentClass}__sidebar nav ul`)
        breadcrumbSelector.addEventListener('click', breadcrumbClick)
        return () => {
            setLocalProjectDetails(null)
            breadcrumbSelector.removeEventListener('click', breadcrumbClick)
        }
    }, [])

    useEffect(() => {
        if (!!projectId) getProjectDetails(projectId, storeIdList)
    }, [projectId, storeIdList])

    useEffect(() => {
        if (projectDetails && projectDetails.storesList) {
            setLocalProjectDetails(projectDetails)
            setProjectType(projectDetails.projectType)
            setSelectedStoreToExecute(projectDetails.storesList[0])
        }
        if (!!projectDetails && projectDetails.enableMessaging) {
            getMessage({
                projectId: projectDetails._id,
                isDetail: true,
                isLoadMore: false,
                pageNum: 1,
            })
        }
    }, [projectDetails])

    useEffect(() => {
        setCustomBreadcrumbs()
    }, [breadcrumbs])

    useEffect(() => {
        if (!!localProjectDetails?._id) {
            setBreadcrumbs([
                {
                    id: 1,
                    label: intl.formatMessage({
                        id: 'modals.news_execution.breadcrumbs.project_execution',
                        defaultMessage: 'Project execution',
                    }),
                    isVisible: true,
                    className: 'project_execution',
                },
            ])
        }
    }, [localProjectDetails])

    const setCustomBreadcrumbs = () => {
        const customBreadcrumbs = getBreadcrumbs()
        if (customBreadcrumbs && customBreadcrumbs.length > 0) {
            customBreadcrumbs.forEach((item, index) => {
                item.id = index
                item.label = intl.formatMessage({
                    id: `modals.new.activity.breadcrumbs.${item.name}`,
                    defaultMessage: item.name,
                })
                item.className = 'activity selectable force-opacity-1'
                item.disabled = false
            })
            breadcrumbs
                .filter((item) => item.isVisible)
                .forEach((item, index) => {
                    customBreadcrumbs.push({ ...item, id: breadcrumbs.length + index })
                })
            setPathArray(customBreadcrumbs)
        }
    }

    return (
        <div className="smart-modals-fullscreen">
            <div className={componentClass} data-isfullscreen={isFullScreen}>
                {/*<div className={`${componentClass}__header`}>
                    <Header
                        onBack={onBack}
                        onClose={onClose}
                        isFullScreen={isFullScreen}
                        onFullScreen={() => setIsFullScreen(!isFullScreen)}
                        backIcon={isMobile ? 'arrow-left' : null}>

                    </Header>
                </div>*/}
                <div className={`${componentClass}__main`}>
                    {!isFullScreen && (
                        <div className={`${componentClass}__sidebar`}>
                            <Sidebar>
                                <div className={`${componentClass}__breadcrumb`}>
                                    <LuxCustomBreadcrumb
                                        pathArray={pathArray}
                                        transparentBg
                                        onClickBreadcrumb={onClickBreadcrumb}
                                    />
                                </div>
                                <h1 className={`${componentClass}__header-mobile`}>
                                    {localProjectDetails?.projectTitle}
                                    {localProjectDetails?.status && (
                                        <LuxBadgeLabel
                                            label={intl.formatMessage({
                                                id: `smart.project.status.${localProjectDetails?.status}`,
                                                defaultMessage: localProjectDetails?.status,
                                            })}
                                            color={statusColors[localProjectDetails?.status]}
                                        />
                                    )}
                                </h1>
                                <VisitProjectSpecs
                                    status={localProjectDetails?.status}
                                    projectTitle={localProjectDetails?.projectTitle}
                                    store={selectedStoreToExecute?.storeName}
                                    startDate={localProjectDetails?.startDate}
                                    projectType={localProjectDetails?.type}
                                    user={localProjectDetails?.creatorUserInfo}
                                    closeDate={localProjectDetails?.closerUserInfo?.date}
                                    remoteVisit={localProjectDetails?.remoteVisit}
                                />
                            </Sidebar>
                        </div>
                    )}
                    <div
                        className={`${componentClass}__content ${
                            isFullScreen ? 'full-screen' : ''
                        }`}>
                        <MainContent hideFooter={true} headerHeight={headerHeight}>
                            {isMobile && (
                                <div className={`${componentClass}__breadcrumb`}>
                                    <LuxCustomBreadcrumb
                                        pathArray={pathArray}
                                        transparentBg
                                        onClickBreadcrumb={onClickBreadcrumb}
                                    />
                                </div>
                            )}
                            {localProjectDetails?.cancelledUserInfo && (
                                <Accordion
                                    id={4}
                                    title={intl.formatMessage({
                                        id: 'modals.visit.execution.accordion.title.cancelled_reasons',
                                        defaultMessage: 'Cancelled Reasons',
                                    })}
                                    extraIcon={'info'}
                                    activeIndex={activeIndexRedoReasons}
                                    setActiveIndex={setActiveIndexRedoReasons}
                                    className={`${componentClass}__accordion ${componentClass}__redo_reasons`}>
                                    <CancelledReasons
                                        actionUserInfo={localProjectDetails.cancelledUserInfo}
                                    />
                                </Accordion>
                            )}
                            {(localProjectDetails?.reasonChoice ||
                                localProjectDetails?.reasonChange ||
                                localProjectDetails?.messageStores) && (
                                <Accordion
                                    id={5}
                                    title={intl.formatMessage({
                                        id: 'modals.new.activity.task.section.editing.reason',
                                        defaultMessage: 'Editing Reason',
                                    })}
                                    extraIcon={'info'}
                                    activeIndex={activeIndexRedoReasons}
                                    setActiveIndex={setActiveIndexRedoReasons}
                                    className={`${componentClass}__accordion ${componentClass}__editing_reasons`}>
                                    <EditingReason
                                        projectType={projectDetails?.projectType}
                                        reasonWhy={
                                            localProjectDetails?.reasonChoice &&
                                            Array.isArray(localProjectDetails?.reasonChoice)
                                                ? localProjectDetails?.reasonChoice
                                                : [localProjectDetails?.reasonChoice]
                                        }
                                        editingReason={localProjectDetails?.reasonChange}
                                        messageForStores={localProjectDetails?.messageStores}
                                        disabled={true}
                                        onReasonWhy={() => null}
                                        onEditingReason={() => null}
                                        onMessage={() => null}
                                    />
                                </Accordion>
                            )}
                            <Accordion
                                id={0}
                                title={intl.formatMessage({
                                    id: 'modals.news_execution.project_specs.title',
                                    defaultMessage: 'Project Specs',
                                })}
                                activeIndex={activeIndexProjectSpecs}
                                setActiveIndex={setActiveIndexProjectSpecs}
                                className={`${componentClass}__accordion ${componentClass}__project-specs-mobile`}>
                                <VisitProjectSpecs
                                    status={localProjectDetails?.status}
                                    projectTitle={localProjectDetails?.projectTitle}
                                    store={selectedStoreToExecute?.storeName}
                                    startDate={localProjectDetails?.startDate}
                                    projectType={localProjectDetails?.type}
                                    user={localProjectDetails?.creatorUserInfo}
                                    closeDate={localProjectDetails?.closerUserInfo?.date}
                                    remoteVisit={localProjectDetails?.remoteVisit}
                                />
                            </Accordion>
                            <Accordion
                                id={1}
                                title={intl.formatMessage({
                                    id: 'modals.news_execution.main_information.title',
                                    defaultMessage: 'Main Information',
                                })}
                                className={`${componentClass}__accordion`}>
                                <MainInformation
                                    title={intl.formatMessage({
                                        id: 'modals.news_execution.main_information.activity_info',
                                        defaultMessage: 'Activity info',
                                    })}
                                    description={localProjectDetails?.information}
                                    attachments={localProjectDetails?.attachments}
                                />
                            </Accordion>
                            <Accordion
                                id={2}
                                title={intl.formatMessage({
                                    id: 'modals.news_execution.action.title',
                                    defaultMessage: 'Action',
                                })}
                                activeIndex={activeIndexAction}
                                setActiveIndex={setActiveIndexAction}
                                className={`${componentClass}__accordion`}>
                                <VisitExecutionAction
                                    storeId={selectedStoreToExecute?.storeId}
                                    projectId={localProjectDetails?._id}
                                    projectName={localProjectDetails?.projectTitle}
                                    projectStatus={localProjectDetails?.status}
                                    surveys={
                                        (localProjectDetails?.storesList[0]?.surveysSvtStores ||
                                            [])[0]?.surveysStore || []
                                    }
                                    user={user}
                                />
                            </Accordion>
                            {!!localProjectDetails && localProjectDetails.enableMessaging && (
                                <Accordion
                                    id={3}
                                    title={intl.formatMessage({
                                        id: 'modals.news_execution.messaging.title',
                                        defaultMessage: 'Messaging',
                                    })}
                                    activeIndex={activeIndexMessaging}
                                    setActiveIndex={setActiveIndexMessaging}
                                    customIcon={
                                        messageList.filter((message) => message.isUnread).length >
                                            0 && (
                                            <div
                                                className={`${componentClass}__messaging-icon`}></div>
                                        )
                                    }
                                    className={`${componentClass}__accordion`}>
                                    <Messaging />
                                </Accordion>
                            )}
                        </MainContent>
                        <div className={`smart-modals-main-content__footer wrapper`}>
                            {
                                <VisitFooter
                                    projectDetails={localProjectDetails}
                                    user={user}
                                    setShowDuplicateDialog={setShowDuplicateDialog}
                                    setShowEditDialogConfirm={setShowEditDialogConfirm}
                                    setShowReassignDialog={setShowReassignDialog}
                                />
                            }
                        </div>
                    </div>
                </div>
                <DialogDuplicate
                    dialogVisible={showDuplicateDialog}
                    onSave={onSaveDialogDuplicate}
                    onHide={() => setShowDuplicateDialog(false)}
                />
                <DialogReject
                    dialogVisible={showRejectDialog}
                    onChange={setRejectReasons}
                    onSave={onRejectConfirm}
                    onHide={() => setShowRejectDialog(false)}
                />
                <DialogEditConfirm
                    dialogVisible={showEditDialogConfirm}
                    onSave={(confirm) => onDialogConfirm(confirm)}
                    onHide={() => setShowEditDialogConfirm(false)}
                    projectName={localProjectDetails?.projectTitle}
                />
                <ReassignDialog
                    visible={showReassignDialog}
                    projectDetails={localProjectDetails}
                    storesList={localProjectDetails?.storesList}
                    delegateWhat={DelegateType.executor}
                    storeId={selectedStoreToExecute?.storeId}
                    hideReassignmentSize={true}
                    disclaimer={intl.formatMessage({
                        id: 'modals.visit.reassign.disclaimer',
                        defaultMessage: `Warning! It is possible to delegate this visit only to a user who has
                        visibility of the selected surveys, otherwise the operation will not be
                        successful`,
                    })}
                    onHide={() => setShowReassignDialog(false)}
                />
            </div>
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    messageList: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].messageList,
    isApproval: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].isApproval,
    isFullScreen: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].isFullScreen,
    projectId: state.layoutReducer.modals.props?.id,
    storeIdList: state.layoutReducer.modals.props?.storeIds,
    surveyId: state.layoutReducer.modals.props?.survey_id,
    projectDetails: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].projectDetails,
    user: state.userReducer.user,
})

const mapDispatchToProps = (dispatch: any) => ({
    onClose: () => {
        dispatch(setMessageList([]))
        dispatch(resetAllForm())
        dispatch(setSelectedStores([]))
        dispatch(
            layoutActions.setModals({
                type: null,
                props: {
                    id: null,
                    storeIdList: null,
                    survey_id: null,
                    visit_id: null,
                    isEdit: false,
                    isClone: false,
                },
            })
        )
    },
    goToEdit: (projectId: string, storeIdList: string, isClone: boolean, isEdit?: boolean) => {
        dispatch(
            layoutActions.setModals({
                type: ModalsType.NEW_ACTIVITY,
                props: { id: projectId, storeIdList, isClone, isEdit },
            })
        )
    },
    setIsFullScreen: (value) => {
        dispatch(setIsFullScreen(value))
    },
    getProjectDetails: (projectId: string, stores?: string[]) => {
        dispatch(getVisitProjectDetails(projectId, stores))
    },
    rejectProject: (projectId: string, message: string, successMessage: string) =>
        dispatch(rejectProject(projectId, message, successMessage)),
    getMessage: (params: GetMessageParams) => dispatch(getMessage(params)),
})

export default connect(mapStateToProps, mapDispatchToProps)(VisitExecution)
